import {Alert, Button, Input, List} from "@chakra-ui/react";
import { Field } from "../../components/ui/field";
import styled from "styled-components";
import { responsive } from "../../media-quries";
import { AsyncSelect } from "chakra-react-select";
import {fetchCities, fetchStreets, getCityById, getStreetById} from "../../services/services";
import {useCallback, useEffect, useState} from "react";
import { FaSearch } from "react-icons/fa";
import {useNavigate, useSearchParams} from "react-router";
import {useUpdateQueryParam} from "../../utils/navigation";
import {parseCityOption, parseStreetOption} from "../../utils/select-options";

export const debounce = (func, delay) => {
    let timeout;
    return (...args) =>
        new Promise((resolve) => {
            clearTimeout(timeout);
            timeout = setTimeout(async () => {
                const result = await func(...args);
                resolve(result);
            }, delay);
        });
};

export const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  .chakra-field__label, .chakra-field__helperText {
    color: white;
  }

  .select, .chakra-input {
    background-color: white;
    border-radius: 2px;
  }
  
  .select div[data-disabled=true] {
    opacity: 0.5;
  }
  
  .select.disabled {
    opacity: 0.5;
  }
`;

export const Miejscowosc = styled(Field)`
  flex-basis: calc(50% - 8px);

  ${responsive.md`
  flex-basis: 100%;
  `}

  ${responsive.sm`
  flex-basis: 100%;
  `}
`;

const Ulica = styled(Field)`
  flex-basis: calc(50% - 8px);

  ${responsive.md`
  flex-basis: 100%;
  `}

  ${responsive.sm`
  flex-basis: 100%;
  `}
`;

const NrBudynku = styled(Field)`
  flex-basis: calc(25% - 12px);

  ${responsive.md`
  flex-basis: calc(50% - 8px);
  `}

  ${responsive.sm`
  flex-basis: 100%;
  `}
`;

const NrLokalu = styled(Field)`
  flex-basis: calc(25% - 12px);

  ${responsive.md`
  flex-basis: calc(50% - 8px);
  `}

  ${responsive.sm`
  flex-basis: 100%;
  `}
`;

const Szukaj = styled(Button)`
  margin-top: 24px;
  flex: 1;
`;

export const Link = styled.span`
  cursor: pointer;
  font-weight: bold;
  text-decoration: underline;
`;


function AddressSearch({ searching }) {
    const [city, setCity] = useState(null);
    const [street, setStreet] = useState(null);
    const [streetInput, setStreetInput] = useState("");
    const [buildingNr, setBuildingNr] = useState(null);
    const [apartmentNr, setApartmentNr] = useState(null);
    const [cityOptions, setCityOptions] = useState([]);
    const [streetOptions, setStreetOptions] = useState([]);
    const [isCityMenuOpen, setIsCityMenuOpen] = useState(false);
    const [isStreetMenuOpen, setIsStreetMenuOpen] = useState(false);
    const [streetRefresh, setStreetRefresh] = useState(0)
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const cityParam = searchParams.get("city");
    const streetParam = searchParams.get("street");
    const buildingNrParam = searchParams.get("buildingNr");
    const apartmentNrParam = searchParams.get("apartmentNr");

    const getCity = (id) => {
        getCityById(id)
            .then((result) => {
                if (result?.result) {
                    setCity(parseCityOption(result.result))
                }
            })
    };

    const getStreet = (id) => {
        getStreetById(id)
            .then((result) => {
                if (result?.result) {
                    setStreet(parseStreetOption(result.result))
                } else {
                    setStreet(id)
                    setStreetInput(id)
                }
            })
    };

    useEffect(() => {
        if (cityParam) {
            getCity(cityParam);
        }

        if (streetParam) {
            getStreet(streetParam);
        }

        if (buildingNrParam) {
            setBuildingNr(buildingNrParam);
        }

        if (apartmentNrParam) {
            setApartmentNr(apartmentNrParam);
        }
    }, [cityParam, streetParam, buildingNrParam, apartmentNrParam])

    const searchCities = async (inputValue) => {
        if (!inputValue || inputValue.length < 3) return [];
        try {
            const response = await fetchCities(inputValue);
            const results = response.result.map((city) => {
                let label = `${city.name}`;
                if (city.gmina_name) {
                    label += `, gm. ${city.gmina_name}`;
                } else {
                    label += `, pow. ${city.powiat_name}`;
                }
                label += `, woj. ${city.wojewodztwo_name}`;

                return {
                    label,
                    value: city.id,
                };
            });
            setCityOptions(results);
            setIsCityMenuOpen(results.length > 0);
            return results;
        } catch (error) {
            console.error("Error fetching cities:", error);
            return [];
        }
    };

    const searchStreets = async (inputValue, city) => {
        if (!inputValue || inputValue.length < 2 || !city) return [];
        try {
            const response = await fetchStreets(inputValue, city);
            const results = response.result.map(parseStreetOption);
            setStreetOptions(results);
            setIsStreetMenuOpen(results.length > 0);
            return results;
        } catch (error) {
            console.error("Error fetching streets:", error);
            return [];
        }
    };

    const debouncedFetchCities = useCallback(debounce(searchCities, 500), []);
    const debouncedFetchStreets = useCallback(debounce((phrase) => {
        return searchStreets(phrase, city?.value);
    }, 500), [city]);
    const updateQueryParams = useUpdateQueryParam();

    return (
        <Container>
            <Miejscowosc label="Miejscowość" required helperText="Zacznij pisać i wybierz z listy">
                <AsyncSelect
                    className="select"
                    value={city}
                    onChange={(val) => {
                        setCity(val);
                        setStreet(null);
                        setStreetInput('');
                        setBuildingNr('');
                        setApartmentNr('');
                        setStreetOptions([]);
                        setIsCityMenuOpen(false);
                    }}
                    variant="searchBar"
                    placeholder="Zacznij pisać..."
                    selectedOptionStyle="check"
                    noOptionsMessage={() => "Brak wyników..."}
                    cacheOptions
                    loadOptions={debouncedFetchCities}
                    isClearable
                    onBlur={() => setIsCityMenuOpen(false)}
                    menuIsOpen={isCityMenuOpen}
                    onInputChange={(inputValue, { action }) => {
                        if (action === "input-change") {
                            setIsCityMenuOpen(inputValue.length >= 3 && cityOptions.length > 0);
                        }
                    }}
                />
            </Miejscowosc>
            <Ulica label="Ulica" helperText="Jeśli nie ma szukanej ulicy, wpisz ją ręcznie">
                <AsyncSelect
                    key={streetRefresh}
                    className={`select ${!city && 'disabled'}`}
                    value={street}
                    inputValue={streetInput}
                    onChange={(val) => {
                        if (!val) {
                            setBuildingNr('');
                            setApartmentNr('');
                        }

                        setStreetInput(val?.label)
                        setStreet(val)
                        setIsStreetMenuOpen(false)
                        setStreetRefresh(streetRefresh + 1)
                    }}
                    isClearable
                    variant="searchBar"
                    placeholder="Zacznij pisać..."
                    selectedOptionStyle="check"
                    noOptionsMessage={() => "Brak wyników..."}
                    onBlur={() => setIsStreetMenuOpen(false)}
                    loadOptions={debouncedFetchStreets}
                    isDisabled={!city}
                    menuIsOpen={isStreetMenuOpen}
                    onInputChange={(inputValue, { action }) => {
                        if (action === "input-change") {
                            setStreetInput(inputValue)
                            setStreet(inputValue)
                            setIsStreetMenuOpen(inputValue.length >= 1 && streetOptions.length > 0);
                        }
                    }}
                />
            </Ulica>
            <NrBudynku label="Numer budynku" required>
                <Input value={buildingNr} onChange={(e) => setBuildingNr(e.target.value)} placeholder="Podaj numer" />
            </NrBudynku>
            <NrLokalu label="Numer lokalu" helperText="Pole opcjonalne">
                <Input value={apartmentNr} onChange={(e) => setApartmentNr(e.target.value)} placeholder="Podaj numer" />
            </NrLokalu>
            <Szukaj variant="solid"
                    disabled={!city || !buildingNr || searching}
                    onClick={() => {
                        if (!city || !buildingNr) {
                            return;
                        }

                        let params = `city=${city.value}`;

                        if (street || street?.value) {
                            params += `&street=${street?.value || street}`;
                        }

                        if (buildingNr) {
                            params += `&buildingNr=${buildingNr}`;
                        }

                        if (apartmentNr) {
                            params += `&apartmentNr=${apartmentNr}`;
                        }

                        navigate(`/szukaj?${params}`);
                    }}>
                <FaSearch /> Szukaj księgi wieczyste
            </Szukaj>
            <Alert.Root colorPalette="accent">
                <Alert.Indicator />
                <Alert.Content>
                    <Alert.Title>Jak szukać po adresie:</Alert.Title>
                    <Alert.Description>
                        <List.Root>
                            <List.Item>Wybierz miejscowość, ulicę, wpisz numer budynku i opcjonalnie numer lokalu</List.Item>
                            <List.Item>Jeśli dla wskazanych kryteriów nie udało Ci się znaleść szukanej księgi spróbuj wyszukiwania{' '}
                                <Link onClick={() => updateQueryParams('searchBy', 'id')}>
                                    po identyfikatorze
                                </Link>{' '}
                                lub po{' '}
                                <Link onClick={() => updateQueryParams('searchBy', 'number')}>
                                    numerze działki
                                </Link>
                                </List.Item>
                        </List.Root>
                    </Alert.Description>
                </Alert.Content>
            </Alert.Root>
        </Container>
    );
}

export default AddressSearch;
